import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { 
  LayoutTemplate, 
  Settings2, 
  Package, 
  Plus,
  Search,
  Edit,
  Trash2,
  Copy,
  ChevronRight,
  DollarSign,
  Layers,
  BarChart3,
  Eye,
  FileUp
} from 'lucide-react';
import TemplateEditor from './TemplateEditor';
import OptionSetEditor from './OptionSetEditor';
import ProductEditorV2 from './ProductEditorV2';
import BulkImportWizard from './BulkImportWizard';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Product type configuration
const PRODUCT_TYPE_CONFIG = {
  LGF: { label: 'Large Format', color: 'bg-blue-500', textColor: 'text-blue-600' },
  SMF: { label: 'Small Format', color: 'bg-green-500', textColor: 'text-green-600' },
  AWD: { label: 'Awards', color: 'bg-amber-500', textColor: 'text-amber-600' },
  PROMO: { label: 'Promotional', color: 'bg-purple-500', textColor: 'text-purple-600' }
};

// Pricing model labels
const PRICING_MODEL_LABELS = {
  per_sqft: 'Per Sq Ft',
  quantity_tiers: 'Quantity Tiers',
  base_plus_options: 'Base + Options'
};

export default function CatalogOverviewV2() {
  const [activeTab, setActiveTab] = useState('overview');
  const [templates, setTemplates] = useState([]);
  const [optionSets, setOptionSets] = useState([]);
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  
  // Editor states
  const [editTemplateId, setEditTemplateId] = useState(null);
  const [showTemplateEditor, setShowTemplateEditor] = useState(false);
  const [editOptionSetId, setEditOptionSetId] = useState(null);
  const [showOptionSetEditor, setShowOptionSetEditor] = useState(false);
  const [editProductId, setEditProductId] = useState(null);
  const [showProductEditor, setShowProductEditor] = useState(false);
  const [showBulkImport, setShowBulkImport] = useState(false);

  // Fetch all data
  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      const [templatesRes, optionSetsRes, productsRes] = await Promise.all([
        fetch(`${API_URL}/api/templates`),
        fetch(`${API_URL}/api/option-sets`),
        fetch(`${API_URL}/api/v2/products`)
      ]);

      const templatesData = await templatesRes.json();
      const optionSetsData = await optionSetsRes.json();
      const productsData = await productsRes.json();

      setTemplates(templatesData.templates || []);
      setOptionSets(optionSetsData.option_sets || []);
      setProducts(productsData.products || []);
    } catch (error) {
      console.error('Error fetching data:', error);
    } finally {
      setLoading(false);
    }
  };

  // Stats calculation
  const stats = {
    templates: templates.length,
    optionSets: optionSets.length,
    products: products.length,
    activeProducts: products.filter(p => p.status === 'active').length,
    byType: {
      LGF: products.filter(p => p.product_type === 'LGF').length,
      SMF: products.filter(p => p.product_type === 'SMF').length,
      AWD: products.filter(p => p.product_type === 'AWD').length,
    }
  };

  // Handle template deletion
  const handleDeleteTemplate = async (templateId) => {
    if (!window.confirm('Are you sure you want to delete this template?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/templates/${templateId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to delete template');
      }
    } catch (error) {
      console.error('Error deleting template:', error);
    }
  };

  // Handle option set deletion
  const handleDeleteOptionSet = async (optionSetId) => {
    if (!window.confirm('Are you sure you want to delete this option set?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/option-sets/${optionSetId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchData();
      } else {
        const data = await res.json();
        alert(data.detail || 'Failed to delete option set');
      }
    } catch (error) {
      console.error('Error deleting option set:', error);
    }
  };

  // Handle product deletion
  const handleDeleteProduct = async (productId) => {
    if (!window.confirm('Are you sure you want to archive this product?')) return;
    
    try {
      const res = await fetch(`${API_URL}/api/v2/products/${productId}`, {
        method: 'DELETE'
      });
      if (res.ok) {
        fetchData();
      }
    } catch (error) {
      console.error('Error deleting product:', error);
    }
  };

  // Filter items by search
  const filteredTemplates = templates.filter(t => 
    t.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    t.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredOptionSets = optionSets.filter(o => 
    o.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    o.code.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const filteredProducts = products.filter(p => 
    p.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    (p.sku && p.sku.toLowerCase().includes(searchTerm.toLowerCase()))
  );

  // Render overview tab
  const renderOverview = () => (
    <div className="space-y-6">
      {/* Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('templates')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Templates</p>
                <p className="text-3xl font-bold">{stats.templates}</p>
              </div>
              <div className="p-3 bg-blue-100 rounded-full">
                <LayoutTemplate className="h-6 w-6 text-blue-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('options')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Option Sets</p>
                <p className="text-3xl font-bold">{stats.optionSets}</p>
              </div>
              <div className="p-3 bg-purple-100 rounded-full">
                <Settings2 className="h-6 w-6 text-purple-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card className="cursor-pointer hover:shadow-md transition-shadow" onClick={() => setActiveTab('products')}>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">Products</p>
                <p className="text-3xl font-bold">{stats.products}</p>
                <p className="text-xs text-green-600">{stats.activeProducts} active</p>
              </div>
              <div className="p-3 bg-green-100 rounded-full">
                <Package className="h-6 w-6 text-green-600" />
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-500">By Type</p>
                <div className="flex gap-2 mt-2">
                  <Badge variant="outline" className="text-blue-600">LGF: {stats.byType.LGF}</Badge>
                  <Badge variant="outline" className="text-green-600">SMF: {stats.byType.SMF}</Badge>
                  <Badge variant="outline" className="text-amber-600">AWD: {stats.byType.AWD}</Badge>
                </div>
              </div>
              <div className="p-3 bg-gray-100 rounded-full">
                <BarChart3 className="h-6 w-6 text-gray-600" />
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Quick Actions */}
      <Card>
        <CardHeader>
          <CardTitle>Quick Actions</CardTitle>
          <CardDescription>Common tasks for managing your product catalog</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            <Button 
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => { setEditTemplateId(null); setShowTemplateEditor(true); }}
            >
              <LayoutTemplate className="h-6 w-6" />
              <span>New Template</span>
            </Button>
            <Button 
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => { setEditOptionSetId(null); setShowOptionSetEditor(true); }}
            >
              <Settings2 className="h-6 w-6" />
              <span>New Option Set</span>
            </Button>
            <Button 
              variant="secondary"
              className="h-auto py-4 flex flex-col items-center gap-2"
              onClick={() => { setEditProductId(null); setShowProductEditor(true); }}
            >
              <Package className="h-6 w-6" />
              <span>New Product</span>
            </Button>
            <Button 
              variant="outline"
              className="h-auto py-4 flex flex-col items-center gap-2 border-dashed border-2 hover:border-blue-500 hover:bg-blue-50"
              onClick={() => setShowBulkImport(true)}
              data-testid="bulk-import-btn"
            >
              <FileUp className="h-6 w-6" />
              <span>Bulk Import</span>
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Recent Templates */}
      <Card>
        <CardHeader className="flex flex-row items-center justify-between">
          <div>
            <CardTitle>Templates</CardTitle>
            <CardDescription>Product blueprints with pricing configurations</CardDescription>
          </div>
          <Button variant="ghost" size="sm" onClick={() => setActiveTab('templates')}>
            View All <ChevronRight className="h-4 w-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {templates.slice(0, 6).map(template => (
              <div 
                key={template.id}
                className="border rounded-lg p-4 hover:shadow-md transition-shadow cursor-pointer"
                onClick={() => { setEditTemplateId(template.id); setShowTemplateEditor(true); }}
              >
                <div className="flex items-start justify-between">
                  <div>
                    <h4 className="font-medium">{template.name}</h4>
                    <p className="text-sm text-gray-500">{template.code}</p>
                  </div>
                  <Badge className={PRODUCT_TYPE_CONFIG[template.product_type]?.color}>
                    {template.product_type}
                  </Badge>
                </div>
                <div className="mt-3 flex items-center gap-2 text-sm text-gray-500">
                  <DollarSign className="h-4 w-4" />
                  <span>{PRICING_MODEL_LABELS[template.pricing?.model] || 'Custom'}</span>
                  <span className="text-gray-300">|</span>
                  <Layers className="h-4 w-4" />
                  <span>{template.product_count || 0} products</span>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );

  // Render templates tab
  const renderTemplates = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <div className="relative w-64">
            <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
            <Input 
              placeholder="Search templates..." 
              className="pl-10"
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
        </div>
        <Button onClick={() => { setEditTemplateId(null); setShowTemplateEditor(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Template
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredTemplates.map(template => (
          <Card key={template.id}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className={`w-12 h-12 rounded-lg flex items-center justify-center ${PRODUCT_TYPE_CONFIG[template.product_type]?.color}`}>
                    <LayoutTemplate className="h-6 w-6 text-white" />
                  </div>
                  <div>
                    <h4 className="font-medium">{template.name}</h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span className="font-mono">{template.code}</span>
                      <span>•</span>
                      <span>{PRICING_MODEL_LABELS[template.pricing?.model]}</span>
                      <span>•</span>
                      <span>{template.pricing?.default_markup_percent}% markup</span>
                      {template.pricing?.fixed_addition > 0 && (
                        <span>+ ${template.pricing.fixed_addition}</span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge variant="outline">{template.product_count || 0} products</Badge>
                  <Badge variant={template.status === 'active' ? 'default' : 'secondary'}>
                    {template.status}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setEditTemplateId(template.id); setShowTemplateEditor(true); }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteTemplate(template.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render option sets tab
  const renderOptionSets = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Search option sets..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={() => { setEditOptionSetId(null); setShowOptionSetEditor(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Option Set
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredOptionSets.map(optionSet => (
          <Card key={optionSet.id}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  <div className="w-12 h-12 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Settings2 className="h-6 w-6 text-purple-600" />
                  </div>
                  <div>
                    <h4 className="font-medium">{optionSet.name}</h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span className="font-mono">{optionSet.code}</span>
                      <span>•</span>
                      <span>{optionSet.choices?.length || 0} choices</span>
                      <span>•</span>
                      <span>{optionSet.type}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    {optionSet.applicable_types?.map(type => (
                      <Badge key={type} variant="outline" className={PRODUCT_TYPE_CONFIG[type]?.textColor}>
                        {type}
                      </Badge>
                    ))}
                  </div>
                  <Badge variant="outline">{optionSet.templates_using || 0} templates</Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setEditOptionSetId(optionSet.id); setShowOptionSetEditor(true); }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteOptionSet(optionSet.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
              {/* Preview choices */}
              <div className="mt-3 flex flex-wrap gap-2">
                {optionSet.choices?.slice(0, 5).map(choice => (
                  <Badge key={choice.id} variant="secondary" className="text-xs">
                    {choice.name}
                    {choice.price_modifier?.value !== 0 && (
                      <span className="ml-1 text-gray-500">
                        ({choice.price_modifier?.type === 'percent' ? `${choice.price_modifier.value}%` : `$${choice.price_modifier.value}`})
                      </span>
                    )}
                  </Badge>
                ))}
                {optionSet.choices?.length > 5 && (
                  <Badge variant="outline" className="text-xs">+{optionSet.choices.length - 5} more</Badge>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>
    </div>
  );

  // Render products tab
  const renderProducts = () => (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="relative w-64">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
          <Input 
            placeholder="Search products..." 
            className="pl-10"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
        </div>
        <Button onClick={() => { setEditProductId(null); setShowProductEditor(true); }}>
          <Plus className="h-4 w-4 mr-2" /> New Product
        </Button>
      </div>

      <div className="grid gap-4">
        {filteredProducts.map(product => (
          <Card key={product.id}>
            <CardContent className="py-4">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-4">
                  {product.images?.primary ? (
                    <img 
                      src={product.images.primary} 
                      alt={product.name}
                      className="w-12 h-12 rounded-lg object-cover"
                    />
                  ) : (
                    <div className="w-12 h-12 rounded-lg bg-gray-100 flex items-center justify-center">
                      <Package className="h-6 w-6 text-gray-400" />
                    </div>
                  )}
                  <div>
                    <h4 className="font-medium">{product.name}</h4>
                    <div className="flex items-center gap-2 mt-1 text-sm text-gray-500">
                      <span className="font-mono">{product.sku}</span>
                      <span>•</span>
                      <span>{product.template_name}</span>
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className={PRODUCT_TYPE_CONFIG[product.product_type]?.color}>
                    {product.product_type}
                  </Badge>
                  <Badge variant={product.status === 'active' ? 'default' : 'secondary'}>
                    {product.status}
                  </Badge>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => { setEditProductId(product.id); setShowProductEditor(true); }}
                  >
                    <Edit className="h-4 w-4" />
                  </Button>
                  <Button 
                    variant="ghost" 
                    size="sm"
                    onClick={() => handleDeleteProduct(product.id)}
                  >
                    <Trash2 className="h-4 w-4 text-red-500" />
                  </Button>
                </div>
              </div>
            </CardContent>
          </Card>
        ))}

        {filteredProducts.length === 0 && (
          <Card>
            <CardContent className="py-12 text-center">
              <Package className="h-12 w-12 mx-auto text-gray-300 mb-4" />
              <h3 className="text-lg font-medium text-gray-600">No products yet</h3>
              <p className="text-sm text-gray-500 mt-1">Create your first product to get started</p>
              <Button 
                className="mt-4"
                onClick={() => { setEditProductId(null); setShowProductEditor(true); }}
              >
                <Plus className="h-4 w-4 mr-2" /> Create Product
              </Button>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      <div className="mb-6">
        <h1 className="text-2xl font-bold">Product Catalog V2</h1>
        <p className="text-gray-500">Template-based product management with unified pricing</p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="mb-6">
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="templates">Templates ({templates.length})</TabsTrigger>
          <TabsTrigger value="options">Option Sets ({optionSets.length})</TabsTrigger>
          <TabsTrigger value="products">Products ({products.length})</TabsTrigger>
        </TabsList>

        <TabsContent value="overview">{renderOverview()}</TabsContent>
        <TabsContent value="templates">{renderTemplates()}</TabsContent>
        <TabsContent value="options">{renderOptionSets()}</TabsContent>
        <TabsContent value="products">{renderProducts()}</TabsContent>
      </Tabs>

      {/* Template Editor Modal */}
      {showTemplateEditor && (
        <TemplateEditor
          templateId={editTemplateId}
          onClose={() => { setShowTemplateEditor(false); setEditTemplateId(null); }}
          onSave={() => { setShowTemplateEditor(false); setEditTemplateId(null); fetchData(); }}
        />
      )}

      {/* Option Set Editor Modal */}
      {showOptionSetEditor && (
        <OptionSetEditor
          optionSetId={editOptionSetId}
          onClose={() => { setShowOptionSetEditor(false); setEditOptionSetId(null); }}
          onSave={() => { setShowOptionSetEditor(false); setEditOptionSetId(null); fetchData(); }}
        />
      )}

      {/* Product Editor Modal */}
      {showProductEditor && (
        <ProductEditorV2
          productId={editProductId}
          onClose={() => { setShowProductEditor(false); setEditProductId(null); }}
          onSave={() => { setShowProductEditor(false); setEditProductId(null); fetchData(); }}
        />
      )}

      {/* Bulk Import Wizard */}
      <BulkImportWizard
        open={showBulkImport}
        onClose={() => setShowBulkImport(false)}
        templates={templates}
        onImportComplete={(result) => {
          fetchData();
          setShowBulkImport(false);
        }}
      />
    </div>
  );
}
